import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { DocumentDuplicateIcon, EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '@/lib/auth';
import { replicasApi } from '@/lib/api';

interface Replica {
  id: string;
  name: string;
  status: 'creating' | 'active' | 'failed';
  connection_url: string | null;
  error_message: string | null;
  database_name: string;
  created_at: string;
}

export default function ProjectReplicas() {
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('Read replica');
  const [showUrl, setShowUrl] = useState(false);

  const { data, isLoading } = useQuery(
    ['replica', projectId],
    () => replicasApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
      refetchInterval: (response) => response?.data?.replica?.status === 'creating' ? 3000 : false,
    }
  );

  const createMutation = useMutation(
    () => replicasApi.create(projectId as string, name),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['replica', projectId]);
        toast.success('Read replica created');
      },
      onError: (error: unknown) => {
        const response = error as { response?: { data?: { message?: string } } };
        toast.error(response.response?.data?.message || 'Failed to create read replica');
        queryClient.invalidateQueries(['replica', projectId]);
      },
    }
  );

  const deleteMutation = useMutation(
    () => replicasApi.remove(projectId as string),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['replica', projectId]);
        toast.success('Read replica removed');
      },
      onError: (error: unknown) => {
        const response = error as { response?: { data?: { message?: string } } };
        toast.error(response.response?.data?.message || 'Failed to remove read replica');
      },
    }
  );

  if (authLoading || isLoading) {
    return <div className="py-16 text-center text-sm text-gray-500">Loading…</div>;
  }

  const replica = data?.data?.replica as Replica | null;
  const runtime = data?.data?.runtime as { streaming: boolean; state: string | null; lagSeconds: number | null } | undefined;

  return (
    <div className="px-4 sm:px-0">
      <h1 className="text-2xl font-semibold text-gray-900">Read replica</h1>
      <p className="mt-2 max-w-2xl text-sm text-gray-600">
        A standby Postgres server replaying this cluster's write-ahead log. Migrations and other schema changes on the primary show up here on their own. The connection is your database on that server, and it cannot write.
      </p>

      {!replica && (
        <form
          className="mt-6 max-w-lg rounded-lg border border-gray-200 bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault();
            createMutation.mutate();
          }}
        >
          <label className="block">
            <span className="form-label">Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="form-input"
              required
            />
          </label>
          <button
            type="submit"
            className="btn-primary mt-4 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Creating…' : 'Create read replica'}
          </button>
        </form>
      )}

      {replica && (
        <section className="mt-6 max-w-2xl rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-gray-900">{replica.name}</h2>
              <p className="mt-1 text-sm text-gray-500">Database {replica.database_name}</p>
              <p className="mt-1 text-sm text-gray-600">
                Standby {runtime?.streaming ? 'is streaming the write-ahead log' : 'is not connected'}
                {runtime?.lagSeconds != null ? ` · lag ${runtime.lagSeconds.toFixed(2)}s` : ''}
              </p>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              replica.status === 'active' ? 'bg-green-100 text-green-800' :
              replica.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {replica.status}
            </span>
          </div>

          {replica.error_message && (
            <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{replica.error_message}</p>
          )}

          {replica.connection_url && (
            <div className="mt-4">
              <span className="form-label">Read-only connection</span>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={showUrl ? replica.connection_url : '••••••••••••••••'}
                  className="form-input font-mono text-sm"
                />
                <button type="button" className="btn-secondary" onClick={() => setShowUrl((current) => !current)} aria-label="Show connection string">
                  {showUrl ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(replica.connection_url || '').then(
                      () => toast.success('Connection copied'),
                      () => toast.error('Could not copy')
                    );
                  }}
                  aria-label="Copy connection string"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">From your machine, use host localhost and port 5433 with this same database, user, and password.</p>
            </div>
          )}

          <button
            type="button"
            className="btn-danger mt-4"
            onClick={() => {
              if (confirm(`Remove ${replica.name}? This forgets the connection. The standby server keeps replaying the log.`)) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isLoading}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            {deleteMutation.isLoading ? 'Removing…' : 'Remove replica'}
          </button>
        </section>
      )}
    </div>
  );
}
